
import { defineComponent } from 'vue'
import { store } from '@/store'
import type { LmsModule, CourseLesson, ScormState } from '@/store/lmsCourse/index'
import { getEnvironmentVariables } from '@training24/course/src/environmentVariables'

export default defineComponent({
  name: 'LmsLessonView',

  data() {
    return {
      completed: false,
      ready: false,
      // „Nochmals ansehen": überstimmt den (persistierten) Abschluss-Status,
      // damit der Player trotz abgeschlossener Lektion wieder angezeigt wird
      replay: false,
    }
  },

  computed: {
    moduleId(): string {
      return this.$route.params.moduleId as string
    },
    lessonId(): string {
      return this.$route.params.lessonId as string
    },
    isFallback(): boolean {
      return this.lessonId === '__course'
    },
    module(): LmsModule | null {
      return store.getters['lmsCourse/getModuleById'](this.moduleId)
    },
    lesson(): CourseLesson | null {
      if (this.isFallback) return null
      const lessons: CourseLesson[] = store.getters['lmsCourse/getLessons'](this.moduleId)
      return lessons.find((l) => l.lessonId === this.lessonId) ?? null
    },
    userId(): string {
      return store.getters['getUserId'] ?? 'anon'
    },
    scormPath(): string | null {
      if (this.isFallback) {
        const m = this.module
        if (!m || !m.lms_path) return null
        return m.lms_path
      }
      const l = this.lesson
      if (!l || !l.s3Key) return null
      return l.s3Key.startsWith('http') ? l.s3Key : `${getEnvironmentVariables().contentBase}/${l.s3Key}`
    },
    isCompleted(): boolean {
      if (this.replay) return false
      if (this.completed) return true
      if (this.isFallback) return store.getters['lmsCourse/isCompleted'](this.moduleId)
      return store.getters['lmsCourse/isLessonCompleted'](this.moduleId, this.lessonId)
    },
  },

  methods: {
    backToModule() {
      this.$router.push({ name: 'lmsModule', params: { moduleId: this.moduleId } })
    },

    onIframeLoad() {
      this.injectIframeCustomizations()
    },

    restartLesson() {
      // completed nicht zurücksetzen: der Abschluss bleibt bestehen (auch im Store).
      // Frische SCORM-API ohne Wiederaufnahme-Daten, damit die Lektion von vorne startet;
      // replay blendet den Player wieder ein (der iframe wird dabei neu erzeugt).
      this.installScormApi(true)
      this.replay = true
    },

    injectIframeCustomizations() {
      const iframe = this.$refs.courseIframe as HTMLIFrameElement
      if (!iframe?.contentDocument) return
      const doc = iframe.contentDocument

      const style = doc.createElement('style')
      style.textContent = `
        body, #root {
          background-color: #ffffff !important;
          color: #23282D !important;
        }
        button[data-testid="next-button"],
        button[data-testid="submit-button"],
        button[data-testid="continue-button"],
        .btn-primary,
        [class*="PrimaryButton"],
        [class*="primary-button"],
        [class*="NextButton"],
        [class*="SubmitButton"] {
          background-color: #0D5295 !important;
          border-color: #0D5295 !important;
          color: #ffffff !important;
        }
        button[data-testid="next-button"]:hover,
        button[data-testid="submit-button"]:hover,
        .btn-primary:hover,
        [class*="PrimaryButton"]:hover {
          background-color: #2790C9 !important;
          border-color: #2790C9 !important;
        }
        button[aria-label="Enter Fullscreen"],
        button[aria-label="Exit Fullscreen"] { display: none !important; }
        @media (min-width: 769px) {
          button[aria-label="Close navigation sidebar"] { display: none !important; }
        }
      `
      doc.head.appendChild(style)

      const translations: Record<string, string> = {
        'Next': 'Weiter', 'Continue': 'Weiter', 'Submit': 'Absenden',
        'Back': 'Zurück', 'Finish': 'Abschließen', 'Retry': 'Wiederholen',
        'Start': 'Starten', 'Check': 'Prüfen',
        'Try again': 'Nochmals versuchen', 'Show answer': 'Antwort anzeigen',
        'Next slide': 'Nächste Folie', 'Previous slide': 'Vorherige Folie',
      }
      function translateButtons(root: Document | Element) {
        root.querySelectorAll("button, [role='button']").forEach((el) => {
          const text = el.textContent && el.textContent.trim()
          if (text && translations[text] && el.childElementCount === 0) {
            el.textContent = translations[text]
          }
        })
      }
      translateButtons(doc)
      const obs = new MutationObserver(() => translateButtons(doc))
      obs.observe(doc.body, { childList: true, subtree: true })
      setTimeout(() => obs.disconnect(), 5 * 60 * 1000)

      if (window.innerWidth >= 769) {
        const tryOpen = () => {
          const btn = doc.querySelector('button[aria-label="Open navigation sidebar"]') as HTMLButtonElement | null
          if (btn) { btn.click(); return true }
          return false
        }
        if (!tryOpen()) {
          const sidebarObs = new MutationObserver(() => { if (tryOpen()) sidebarObs.disconnect() })
          sidebarObs.observe(doc.body, { childList: true, subtree: true })
          setTimeout(() => sidebarObs.disconnect(), 10_000)
        }
      }
    },

    /** Schlüssel für die Ablage des SCORM-Stands (Lektion bzw. „__course" beim Fallback) */
    scormStateKey(): string {
      return this.isFallback ? '__course' : this.lessonId
    },

    /**
     * Gespeicherten Stand aus dem Store in ein CMI-Objekt überführen (für Wiederaufnahme).
     * Mit `fresh` wird der gespeicherte Stand ignoriert – die Lektion startet von vorne.
     */
    buildInitialCmi(fresh = false): Record<string, string> {
      const cmi: Record<string, string> = {}
      const state: ScormState | null = fresh
        ? null
        : store.getters['lmsCourse/getScormState'](this.moduleId, this.scormStateKey())
      // Gültige Zeichenkette? Schützt vor korrupten Altständen, in denen "undefined"/"null"
      // gespeichert wurde – sonst stürzt das Paket beim JSON.parse(suspend_data) ab.
      const valid = (v?: string | null): v is string =>
        v !== undefined && v !== null && v !== '' && v !== 'undefined' && v !== 'null'
      if (state) {
        const both = (k12: string, k04: string, v?: string | number) => {
          if (v === undefined || v === null) return
          const s = String(v)
          if (!valid(s)) return
          cmi[k12] = s
          cmi[k04] = s
        }
        both('cmi.core.lesson_location', 'cmi.location', state.location)
        if (valid(state.suspendData)) cmi['cmi.suspend_data'] = state.suspendData
        both('cmi.core.lesson_status', 'cmi.completion_status', state.lessonStatus)
        if (valid(state.successStatus)) cmi['cmi.success_status'] = state.successStatus
        both('cmi.core.score.raw', 'cmi.score.raw', state.scoreRaw)
        both('cmi.core.score.min', 'cmi.score.min', state.scoreMin)
        both('cmi.core.score.max', 'cmi.score.max', state.scoreMax)
        if (state.scoreScaled !== undefined) cmi['cmi.score.scaled'] = String(state.scoreScaled)
        if (state.progressMeasure !== undefined) cmi['cmi.progress_measure'] = String(state.progressMeasure)
        both('cmi.core.total_time', 'cmi.total_time', state.totalTime)
        // Antworten wiederherstellen, damit das Paket sie kennt
        ;(state.interactions ?? []).forEach((it, i) => {
          const p = `cmi.interactions.${i}.`
          if (it.id !== undefined) cmi[p + 'id'] = it.id
          if (it.type !== undefined) cmi[p + 'type'] = it.type
          if (it.studentResponse !== undefined) {
            cmi[p + 'student_response'] = it.studentResponse
            cmi[p + 'learner_response'] = it.studentResponse
          }
          if (it.result !== undefined) cmi[p + 'result'] = it.result
          if (it.weighting !== undefined) cmi[p + 'weighting'] = String(it.weighting)
          if (it.latency !== undefined) cmi[p + 'latency'] = it.latency
          if (it.description !== undefined) cmi[p + 'description'] = it.description
        })
        cmi['cmi.interactions._count'] = String((state.interactions ?? []).length)
      }
      // Eintrittsart: „resume" signalisiert dem Paket, an der letzten Stelle fortzufahren
      const hasResume = !!(state && (valid(state.suspendData) || valid(state.location)))
      cmi['cmi.core.entry'] = hasResume ? 'resume' : 'ab-initio'
      cmi['cmi.entry'] = hasResume ? 'resume' : 'ab-initio'
      return cmi
    },

    /** Standardwerte für nicht gesetzte CMI-Schlüssel (SCORM 1.2 + 2004) */
    scormDefault(key: string): string {
      if (key.endsWith('._count')) return '0'
      if (key.endsWith('._children')) return ''
      switch (key) {
        case 'cmi.core.lesson_status': return 'not attempted'
        case 'cmi.completion_status': return 'unknown'
        case 'cmi.success_status': return 'unknown'
        case 'cmi.core.credit':
        case 'cmi.credit': return 'credit'
        case 'cmi.core.lesson_mode':
        case 'cmi.mode': return 'normal'
        case 'cmi.core.entry':
        case 'cmi.entry': return 'ab-initio'
        case 'cmi.core.student_id':
        case 'cmi.learner_id': return this.userId
        default: return ''
      }
    },

    /** Markiert die Lektion/den Kurs als abgeschlossen (einmalig) */
    handleCompletion() {
      // Nach „Nochmals ansehen" den Abschluss-Screen wieder anzeigen dürfen
      this.replay = false
      if (this.completed) return
      if (this.isFallback) {
        store.dispatch('lmsCourse/MARK_COMPLETED', { userId: this.userId, moduleId: this.moduleId })
      } else {
        store.dispatch('lmsCourse/MARK_LESSON_COMPLETED', { userId: this.userId, moduleId: this.moduleId, lessonId: this.lessonId })
      }
      this.completed = true
    },

    /** Liest den aktuellen CMI-Stand aus und persistiert ihn über den Store */
    persistScorm() {
      const cmi: Record<string, string> = (this as any)._cmi
      if (!cmi) return
      const num = (v?: string): number | undefined => {
        if (v === undefined || v === '') return undefined
        const n = parseFloat(v)
        return isNaN(n) ? undefined : n
      }
      // Antworten einsammeln (Indizes per Scan, da nicht jedes Paket _count pflegt)
      const indices = new Set<number>()
      Object.keys(cmi).forEach((k) => {
        const m = k.match(/^cmi\.interactions\.(\d+)\./)
        if (m) indices.add(parseInt(m[1], 10))
      })
      const interactions = Array.from(indices).sort((a, b) => a - b).map((i) => {
        const p = `cmi.interactions.${i}.`
        const inter: Record<string, any> = { id: cmi[p + 'id'] ?? String(i) }
        if (cmi[p + 'type'] !== undefined) inter.type = cmi[p + 'type']
        const resp = cmi[p + 'student_response'] ?? cmi[p + 'learner_response']
        if (resp !== undefined) inter.studentResponse = resp
        if (cmi[p + 'result'] !== undefined) inter.result = cmi[p + 'result']
        if (cmi[p + 'correct_responses.0.pattern'] !== undefined) inter.correctResponse = cmi[p + 'correct_responses.0.pattern']
        if (cmi[p + 'weighting'] !== undefined) inter.weighting = num(cmi[p + 'weighting'])
        if (cmi[p + 'latency'] !== undefined) inter.latency = cmi[p + 'latency']
        if (cmi[p + 'description'] !== undefined) inter.description = cmi[p + 'description']
        return inter
      })

      const state: Record<string, any> = {
        location: cmi['cmi.core.lesson_location'] ?? cmi['cmi.location'],
        suspendData: cmi['cmi.suspend_data'],
        lessonStatus: cmi['cmi.core.lesson_status'] ?? cmi['cmi.completion_status'],
        successStatus: cmi['cmi.success_status'],
        scoreRaw: num(cmi['cmi.core.score.raw'] ?? cmi['cmi.score.raw']),
        scoreMin: num(cmi['cmi.core.score.min'] ?? cmi['cmi.score.min']),
        scoreMax: num(cmi['cmi.core.score.max'] ?? cmi['cmi.score.max']),
        scoreScaled: num(cmi['cmi.score.scaled']),
        progressMeasure: num(cmi['cmi.progress_measure']),
        sessionTime: cmi['cmi.core.session_time'] ?? cmi['cmi.session_time'],
        totalTime: cmi['cmi.core.total_time'] ?? cmi['cmi.total_time'],
        interactions: interactions.length ? interactions : undefined,
      }
      // Leere/undefinierte Felder entfernen – auch die wörtlichen "undefined"/"null",
      // damit kein Junk gespeichert wird, der beim Resume das JSON.parse des Pakets sprengt.
      Object.keys(state).forEach((k) => {
        const v = state[k]
        if (v === undefined || v === '' || v === 'undefined' || v === 'null') delete state[k]
      })
      if (Object.keys(state).length === 0) return

      store.dispatch('lmsCourse/SAVE_SCORM_STATE', {
        userId: this.userId,
        moduleId: this.moduleId,
        lessonId: this.scormStateKey(),
        state,
      })
    },

    installScormApi(fresh = false) {
      const cmi: Record<string, string> = this.buildInitialCmi(fresh)
      ;(this as any)._cmi = cmi

      const getValue = (key: string): string => {
        const v = cmi[key]
        // Niemals die wörtlichen "undefined"/"null" zurückgeben – das Paket würde daran
        // beim JSON.parse scheitern. Stattdessen den SCORM-Standardwert liefern.
        if (v === undefined || v === 'undefined' || v === 'null') return this.scormDefault(key)
        return v
      }
      const setValue = (key: string, value: string): string => {
        cmi[key] = value
        if (
          (key === 'cmi.core.lesson_status' && (value === 'passed' || value === 'completed')) ||
          (key === 'cmi.completion_status' && value === 'completed') ||
          (key === 'cmi.success_status' && value === 'passed')
        ) {
          this.handleCompletion()
        }
        return 'true'
      }

      // SCORM 1.2
      ;(window as any).API = {
        LMSInitialize: (_: string) => 'true',
        LMSFinish: (_: string) => { this.persistScorm(); return 'true' },
        LMSGetValue: (key: string) => getValue(key),
        LMSSetValue: (key: string, value: string) => setValue(key, value),
        LMSCommit: (_: string) => { this.persistScorm(); return 'true' },
        LMSGetLastError: () => '0',
        LMSGetErrorString: (_: string) => 'No Error',
        LMSGetDiagnostic: (_: string) => 'No Error',
      }

      // SCORM 2004
      ;(window as any).API_1484_11 = {
        Initialize: (_: string) => 'true',
        Terminate: (_: string) => { this.persistScorm(); return 'true' },
        GetValue: (key: string) => getValue(key),
        SetValue: (key: string, value: string) => setValue(key, value),
        Commit: (_: string) => { this.persistScorm(); return 'true' },
        GetLastError: () => '0',
        GetErrorString: (_: string) => 'No Error',
        GetDiagnostic: (_: string) => 'No Error',
      }
    },
  },

  async mounted() {
    await store.dispatch('lmsCourse/LOAD_CATALOG')
    await Promise.all([
      store.dispatch('lmsCourse/LOAD_PROGRESS', { userId: this.userId, moduleId: this.moduleId }),
      store.dispatch('lmsCourse/LOAD_LESSONS', this.moduleId),
    ])
    // Erst nach dem Laden des gespeicherten Stands die API bereitstellen und den Player rendern,
    // damit das Paket beim Start die Wiederaufnahme-Daten lesen kann.
    this.installScormApi()
    this.ready = true
  },

  beforeUnmount() {
    // Letzten Stand sichern, falls das Paket nicht selbst committed hat
    this.persistScorm()
    delete (window as any).API
    delete (window as any).API_1484_11
  },
})
